import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import SuiTypography from "components/SuiTypography";
import SuiBox from "components/SuiBox";
import Grid from "@mui/material/Grid";
import DatePicker from "react-datepicker";
import { toast } from "react-toastify";
import {
  calculateServiceUnpaidBalance,
  getServiceTotalAmountIncludingGst,
} from "utils/common";
import { updateClientBalanceReceivableDate } from "services/clientdetails";
import "react-datepicker/dist/react-datepicker.css";

/**
 * Maps Firestore or string dates to a Date for the picker.
 * @param {import("firebase/firestore").Timestamp | string | Date | null | undefined} value
 * @returns {Date | null}
 */
function toPickerDate(value) {
  if (value == null) {
    return null;
  }
  if (typeof value === "object" && value.seconds !== undefined) {
    return new Date(value.seconds * 1000);
  }
  return value instanceof Date ? value : new Date(value);
}

function renderCalendarInBody({ children }) {
  if (typeof document === "undefined") {
    return children;
  }
  return createPortal(children, document.body);
}

function AccountStat({
  accounts,
  clientId,
  balanceReceivableDate,
  onBalanceReceivableDateSaved,
}) {
  const [paid, setPaid] = useState(0);
  const [balance, setBalance] = useState(0);
  const [savingDate, setSavingDate] = useState(false);

  useEffect(() => {
    let balanceAmount = 0;
    let paidAmount = 0;
    accounts.forEach((item) => {
      paidAmount += parseFloat(item.amountPaid ?? 0);
      balanceAmount += parseFloat(
        calculateServiceUnpaidBalance(
          getServiceTotalAmountIncludingGst(item),
          item.discount,
          item.amountPaid
        )
      );
    });
    setPaid(paidAmount.toFixed(2));
    setBalance(balanceAmount.toFixed(2));
  }, [accounts]);

  const persistReceivableDate = async (nextDate) => {
    if (!clientId) {
      return;
    }
    try {
      setSavingDate(true);
      await updateClientBalanceReceivableDate({
        clientId,
        balanceReceivableDate: nextDate,
      });
      onBalanceReceivableDateSaved?.();
    } catch (e) {
      console.error("updateClientBalanceReceivableDate failed", {
        clientId,
        error: e,
      });
      toast.error("Could not save balance receivable date.");
    } finally {
      setSavingDate(false);
    }
  };

  return (
    <Grid
      container
      spacing={2}
      sx={{
        mb: 2,
        px: { xs: 2, sm: 2.5, md: 3 },
        alignItems: "stretch",
      }}
    >
      <Grid item xs={12} md={4}>
        <SuiBox sx={statCardSx}>
          <SuiTypography variant="h8" fontWeight="medium" sx={statLabelSx}>
            Total Amount Paid:
          </SuiTypography>
          <SuiTypography variant="h9" fontWeight="medium" sx={statValueSx}>
            {paid}
          </SuiTypography>
        </SuiBox>
      </Grid>
      <Grid item xs={12} md={4}>
        <SuiBox sx={statCardSx}>
          <SuiTypography variant="h8" fontWeight="medium" sx={statLabelSx}>
            Balance:
          </SuiTypography>
          <SuiTypography variant="h9" fontWeight="medium" sx={balanceValueSx}>
            {balance}
          </SuiTypography>
        </SuiBox>
      </Grid>
      <Grid item xs={12} md={4}>
        <SuiBox sx={{ ...statCardSx, gap: 1.25 }}>
          <SuiTypography variant="h8" fontWeight="medium" sx={statLabelSx}>
            Balance Receivable Date:
          </SuiTypography>
          <SuiBox sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
            <DatePicker
              selected={toPickerDate(balanceReceivableDate)}
              onChange={(date) => persistReceivableDate(date)}
              dateFormat="dd/MM/yyyy"
              placeholderText="Optional"
              disabled={savingDate || !clientId}
              className="date-picker-custom-v1"
              popperContainer={renderCalendarInBody}
              popperProps={{ strategy: "fixed" }}
              isClearable
            />
          </SuiBox>
        </SuiBox>
      </Grid>
    </Grid>
  );
}

export default AccountStat;

/** Shared light stat tile: equal height row, content vertically centered. */
const statCardSx = {
  borderRadius: 1,
  display: "flex",
  flexDirection: { xs: "column", sm: "row" },
  alignItems: { xs: "flex-start", sm: "center" },
  justifyContent: { sm: "flex-start" },
  flexWrap: "wrap",
  gap: 0.75,
  rowGap: 0.75,
  minHeight: 52,
  height: "100%",
  p: 1.5,
  boxSizing: "border-box",
  background: "#F8F9FA",
};

const statLabelSx = { color: "#3A416F" };
const statValueSx = { color: "#3A416F" };
const balanceValueSx = { color: "#e53935" };
